/// <amd-module name="Wit/QueriesHub/Common/Services/IQueryChartService" />

import {
    ChartConfiguration,
    ChartConfigurationResponse,
    ColorConfiguration,
    DataServiceCapabilities,
    NameLabelPair
} from "Tfs/Charting/Contracts/Reporting";
import { getReportingClient } from "Tfs/Charting/RestClient/Reporting";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import * as ChartCommon from "Wit/QueriesHub/Charts/Common/ChartCommon";
import { ChartPropertyMap, ChartType } from "Wit/QueriesHub/Charts/Common/ChartTemplate";
import { IQueryDisplayColumn } from "Wit/QueriesHub/Common/Interfaces";

export interface IQueryChartService extends IVssService {
    getCapabilities(): Promise<DataServiceCapabilities>;
    /**
     * Returns a list of fields that can be used for the group by property of a chart.
     */
    getGroupableFields(capabilities: DataServiceCapabilities, queryColumns: IQueryDisplayColumn[]): NameLabelPair[];

    /**
     * Returns a list of fields that can be used in aggregation functions.
     */
    getAggregatableFields(capabilities: DataServiceCapabilities, queryColumns: IQueryDisplayColumn[]): NameLabelPair[];

    /**
     * Fetch data to render charts.
     * @param projectId
     * @param chartConfigs
     */
    getChartData(projectId: string, chartConfigs: ChartConfiguration[]): Promise<ChartCommon.ITransformQueryResults>;

    /**
     * Add a chart to a query.
     */
    addChart(
        projectId: string,
        queryId: string,
        chartType: ChartType,
        title: string,
        chartProperties: ChartPropertyMap,
        userColors: ColorConfiguration[] | null
    ): Promise<ChartConfigurationResponse>;

    /**
     * Edit a query chart
     */
    editChart(
        projectId: string,
        queryId: string,
        chartId: string,
        transformId: string,
        chartType: ChartType,
        title: string,
        chartProperties: ChartPropertyMap,
        userColors: ColorConfiguration[] | null
    ): Promise<ChartConfigurationResponse>;
}

class QueryChartService extends VssService implements IQueryChartService {
    private _capabilities: DataServiceCapabilities | undefined;

    public async getCapabilities(): Promise<DataServiceCapabilities> {
        if (!this._capabilities) {
            const reportingClient = getReportingClient(this.pageContext);
            this._capabilities = (await reportingClient.getDataServiceCapabilities("WorkitemTracking.Queries")).dataServiceCapabilities;
        }
        return this._capabilities;
    }

    public getGroupableFields(capabilities: DataServiceCapabilities, queryColumns: IQueryDisplayColumn[]): NameLabelPair[] {
        const groupableFields = new Set<string>();
        for (const f of capabilities.fields) {
            // TODO: figure out grouping by tags
            if (f.isGroupable) {
                groupableFields.add(f.name);
            }
        }
        const groupableColumns = queryColumns.filter(c => c.name && groupableFields.has(c.name));
        return groupableColumns.map(c => ({ name: c.name as string, labelText: c.text as string }));
    }

    public getAggregatableFields(capabilities: DataServiceCapabilities, queryColumns: IQueryDisplayColumn[]) {
        const aggregatableFields = new Set<string>();
        for (const f of capabilities.fields) {
            if (f.isAggregatable) {
                aggregatableFields.add(f.name);
            }
        }
        const aggregatableColumns = queryColumns.filter(c => c.name && aggregatableFields.has(c.name));
        return aggregatableColumns.map(c => ({ name: c.name as string, labelText: c.text as string }));
    }

    public async getChartData(projectId: string, chartConfigs: ChartConfiguration[]) {
        const reportingClient = getReportingClient(this.pageContext);
        const results = (await reportingClient.runTransformQuery(
            chartConfigs.map(c => ({
                ...c.transformOptions,
                filterContext: {
                    projectId
                }
            })),
            projectId,
            ChartCommon.scope
        )) as any as ChartCommon.ITransformQueryResults;
        return results;
    }

    public async addChart(
        projectId: string,
        queryId: string,
        chartType: ChartType,
        title: string,
        chartProperties: ChartPropertyMap,
        userColors: ColorConfiguration[] | null
    ) {
        const reportingClient = getReportingClient(this.pageContext);
        const chartConfiguration = this._buildChartConfiguration(queryId, chartType, title, chartProperties, userColors);
        return reportingClient.createChartConfiguration(chartConfiguration, projectId);
    }

    public async editChart(
        projectId: string,
        queryId: string,
        chartId: string,
        transformId: string,
        chartType: ChartType,
        title: string,
        chartProperties: ChartPropertyMap,
        userColors: ColorConfiguration[] | null
    ) {
        const reportingClient = getReportingClient(this.pageContext);
        const chartConfiguration = this._buildChartConfiguration(queryId, chartType, title, chartProperties, userColors);
        chartConfiguration.chartId = chartId;
        chartConfiguration.transformOptions.transformId = transformId;
        return reportingClient.replaceChartConfiguration(chartConfiguration, projectId);
    }

    private _buildChartConfiguration(
        queryId: string,
        chartType: ChartType,
        title: string,
        chartProperties: ChartPropertyMap,
        userColors: ColorConfiguration[] | null
    ): ChartConfiguration {
        // exclude those properties that aren't actually required, despite what the typings may say
        const chartConfiguration: Omit<ChartConfiguration, "chartId" | "userColors"> & {
            chartId?: string;
            userColors: ColorConfiguration[] | null;
        } = {
            chartType: chartType,
            groupKey: queryId,
            scope: ChartCommon.scope,
            title,
            transformOptions: {
                groupBy: "",
                ...chartProperties,
                filter: queryId
            } as any,
            userColors
        };

        return chartConfiguration as any;
    }
}

Services.add("IQueryChartService", { serviceFactory: QueryChartService });
